import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Switch } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { serviceManagementService } from '../../services/serviceManagementService';

interface ServiceItem {
  service_id: number;
  name: string;
  category_id?: number;
  category_name?: string;
  price?: number;
  duration?: number;
  display_order?: number;
  status?: number; // 1 active, 0 inactive
}

const ServiceList: React.FC = () => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        console.log('Starting to fetch services...');
        setLoading(true);
        const data = await serviceManagementService.listServices();
        console.log('Fetched services data:', data);
        if (Array.isArray(data)) {
          setServices(data);
        } else {
          console.error('Unexpected data format:', data);
          // Try to handle different response formats
          if (data && data.data && Array.isArray(data.data)) {
            setServices(data.data);
          } else if (data && data.services && Array.isArray(data.services)) {
            setServices(data.services);
          } else {
            console.error('Could not parse services data');
          }
        }
      } catch (error) {
        const e = error as AxiosError;
        console.error('Failed to fetch services', e);
        // Log more detailed error information
        if (e.response) {
          console.error('Error response data:', e.response.data);
          console.error('Error status:', e.response.status);
          console.error('Error headers:', e.response.headers);
        } else if (e.request) {
          console.error('No response received:', e.request);
        } else {
          console.error('Error message:', e.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const toggleStatus = async (row: ServiceItem) => {
    const newStatus = row.status === 1 ? 0 : 1;
    try {
      await serviceManagementService.updateServiceStatus(row.service_id, newStatus);
      setServices((prev) => prev.map((s) => s.service_id === row.service_id ? { ...s, status: newStatus } : s));
    } catch (e) {
      console.error('Failed to update status', e);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
       
         <Typography variant="h4"  fontWeight="bold">Service Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/services/new')}>
          Add Service
        </Button>
      </Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No services found</TableCell>
                </TableRow>
              ) : (
                services.map((s) => (
                  <TableRow key={s.service_id} hover>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.category_name ?? '-'}</TableCell>
                    <TableCell>{s.duration || '-'}</TableCell>
                    <TableCell>{s.price ?? '-'}</TableCell>
                    <TableCell>{s.status === 1 ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <IconButton aria-label="edit" onClick={() => navigate(`/service-management/${s.service_id}`)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ServiceList;
